import type { AxisSelection, DTOOf, IFileWithProcessing, IFileWithTags, PaginatedResponse } from '@atlas/types'
import axios from 'axios'
import type { ApplicableRule } from '@/domain/asset-detail.types'
import { apiClient } from './api.client'
import type { ClassifyTuple } from './classification.service'

/**
 * API-facing file shape: `IFile` with dates serialized to ISO strings, plus
 * the per-concern `processingResults`/`thumbnailStatus` block returned by
 * `GET /files/{id}` and the confirm-upload response.
 */
export type FileDTO = DTOOf<IFileWithProcessing>

/** A file plus its classification tags, as returned by `POST /files/discover`. */
export type FileWithTagsDTO = DTOOf<IFileWithTags>

/**
 * Base64 SHA-256 of a blob, in the encoding S3's `x-amz-checksum-sha256` expects.
 *
 * Reads the whole blob into memory to hash it, because `crypto.subtle.digest` has no
 * streaming form — the upload is therefore bounded by what one ArrayBuffer can hold,
 * which is the same ceiling the existing single-PUT upload already had.
 *
 * `crypto.subtle` requires a secure context. localhost and https both qualify, so this
 * only fails on a plain-http non-local host, where the throw is deliberate: uploading
 * without the header would 403 at S3 with a far less obvious message.
 */
async function sha256Base64(body: Blob): Promise<string> {
	if (!globalThis.crypto?.subtle) {
		throw new Error('Uploads require a secure context (https or localhost) to checksum the file.')
	}
	// Wrapped in a view rather than passed as the raw ArrayBuffer: under jsdom the buffer a
	// Blob hands back belongs to another realm, and WebCrypto's BufferSource check rejects it
	// on identity even though `instanceof ArrayBuffer` holds. A TypedArray is an equally valid
	// BufferSource and is accepted in both realms.
	const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(await body.arrayBuffer()))
	// Chunked rather than one spread of the whole array: `String.fromCharCode(...bytes)`
	// blows the argument limit on large inputs, and a digest is small but the idiom is
	// the one that bit us elsewhere.
	let binary = ''
	for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte)
	return btoa(binary)
}

export interface UploadUrlResponse {
	file: FileDTO
	uploadUrl: string
	expiresIn: number
}

export interface ThumbnailUrlResponse {
	thumbnailUrl: string
	expiresIn: number
}

/** One row of the SUPER_ADMIN file-processing diagnostics listing. */
export interface AdminConcernRow {
	resultId: string
	fileId: string
	fileName: string | null
	/** Lets the page pick a type-appropriate placeholder when there is no thumbnail. */
	contentType: string | null
	/** Presigned preview, absent when the file has no thumbnail. */
	thumbnailUrl?: string
	/** The file was soft-deleted; its concern rows are kept for diagnostics. */
	fileDeleted: boolean
	concernId: string
	status: 'pending' | 'running' | 'completed' | 'failed'
	errorMessage: string | null
	startedAt: string | null
	completedAt: string | null
	createdAt: string
	/** Server-computed with the same cutoffs the stall sweep applies. */
	stale: boolean
}

export const fileService = {
	// `idempotencyKey` is optional and passed through untouched — the backend's
	// create path accepts an optional UUID and returns the existing File for a
	// repeated key. Callers that omit it send exactly the request they did before.
	//
	// `isPublic` routes the object to the public bucket, whose URLs never expire.
	// It defaults to `false` server-side, so omitting it is the private upload every
	// existing caller already performs. Pass it only for content whose URL has to
	// outlive a presigned window — an article's inline images, which are embedded in
	// stored HTML and would otherwise break after the signature expired.
	//
	// `body`, when passed, is hashed and the digest sent as `checksum`, so the presigned
	// URL is bound to exactly those bytes: S3 then refuses any other content, and the
	// SHA-256 the asset page shows is verified rather than self-reported. `putToS3` must
	// then be given the SAME blob — see its note.
	requestUploadUrl: async (input: {
		fileName: string
		contentType: string
		idempotencyKey?: string
		isPublic?: boolean
		body?: Blob
	}): Promise<UploadUrlResponse> => {
		const { body, ...rest } = input
		const response = await apiClient.post<UploadUrlResponse>('/files/upload-url', {
			...rest,
			...(body ? { checksum: await sha256Base64(body) } : {}),
		})
		return response.data
	},

	// `POST /files` — creates the File row only. No presign, no S3 call, no
	// classification; the caller follows with `requestUploadUrlForFile`.
	//
	// `idempotencyKey`, when given, makes the create replay-safe: the same key
	// with the same fileName/contentType/isPublic/declaredFileSize resolves to
	// the File already created for it, and the same key with any other creation
	// identity is refused with a 409 rather than returning a mismatched File.
	// It is omitted from the body entirely when absent, so a caller that does not
	// use it sends exactly the request it always did.
	createFile: async (input: {
		fileName: string
		contentType: string
		isPublic: boolean
		declaredFileSize: number
		idempotencyKey?: string
	}): Promise<FileDTO> => {
		const { idempotencyKey, ...rest } = input
		const response = await apiClient.post<{ file: FileDTO }>('/files', {
			...rest,
			...(idempotencyKey ? { idempotencyKey } : {}),
		})
		return response.data.file
	},

	// `POST /files/{id}/upload-url` — presigns for an already-created File.
	// `body` is the same browser File the caller will PUT next. Hashing it here
	// (not at createFile) binds the presigned URL to these exact bytes — the
	// same checksum-binding guarantee `requestUploadUrl` provides today, just
	// computed at the point the transfer is actually about to start.
	requestUploadUrlForFile: async (fileId: string, body: Blob): Promise<{ uploadUrl: string; file: FileDTO }> => {
		const response = await apiClient.post<{ uploadUrl: string; file: FileDTO }>(`/files/${fileId}/upload-url`, {
			checksum: await sha256Base64(body),
		})
		return response.data
	},

	// Raw axios (NOT apiClient): the pre-signed URL is the credential, and S3
	// rejects requests carrying an unexpected Authorization header. The
	// Content-Type must match the one the URL was signed with. `body` is a Blob
	// so non-`File` byte sources work; a browser `File` still satisfies it.
	//
	// When the presign was given a `body`, the URL is bound to that digest and the
	// matching `x-amz-checksum-sha256` header is MANDATORY — so this recomputes it from
	// the blob it is handed. Pass the same bytes to both calls: a different blob here
	// hashes differently and S3 rejects the signature.
	putToS3: async (
		uploadUrl: string,
		body: Blob,
		onProgress: (percent: number) => void,
		options?: { readonly contentType?: string; readonly signal?: AbortSignal },
	): Promise<void> => {
		await axios.put(uploadUrl, body, {
			headers: {
				'Content-Type': options?.contentType ?? body.type,
				'x-amz-checksum-sha256': await sha256Base64(body),
			},
			onUploadProgress: (event) => {
				if (event.total) {
					onProgress(Math.round((event.loaded / event.total) * 100))
				}
			},
			signal: options?.signal,
		})
	},

	// `classify`, when passed and non-empty, is applied server-side as a
	// tracked, retryable `classification` concern — the caller never needs a
	// separate `/classify/batch` call for it. Existing call sites that pass
	// nothing are unaffected (an empty POST body, exactly as before).
	confirmUpload: async (fileId: string, classify?: ClassifyTuple[]): Promise<FileDTO> => {
		const response = await apiClient.post<FileDTO>(
			`/files/${fileId}/confirm-upload`,
			classify && classify.length > 0 ? { classify } : undefined,
		)
		return response.data
	},

	listFiles: async (): Promise<FileDTO[]> => {
		// Request the endpoint's max page size (newest-first) so the browse list
		// isn't silently truncated at the default limit of 20.
		const response = await apiClient.get<FileDTO[]>('/files', { params: { limit: 100 } })
		// The API returns 204 with no body when the user has no files yet.
		return response.status === 204 || !response.data ? [] : response.data
	},

	getFile: async (fileId: string): Promise<FileDTO> => {
		const response = await apiClient.get<FileDTO>(`/files/${fileId}`)
		return response.data
	},

	// Same as getFile, plus the file's classification tags — resolves one file
	// directly by id rather than requiring callers to page through `discover`
	// (which hard-caps `limit` at 100 and would silently miss a file sitting
	// past the first page).
	getFileWithTags: async (fileId: string): Promise<FileWithTagsDTO> => {
		const response = await apiClient.get<FileWithTagsDTO>(`/files/${fileId}/with-tags`)
		return response.data
	},

	getApplicableRules: async (fileId: string): Promise<ApplicableRule[]> => {
		const response = await apiClient.get<ApplicableRule[]>(`/files/${fileId}/applicable-rules`)
		// The API returns 204 with no body when no rules apply.
		return response.status === 204 || !response.data ? [] : response.data
	},

	getThumbnailUrl: async (fileId: string): Promise<ThumbnailUrlResponse> => {
		const response = await apiClient.get<ThumbnailUrlResponse>(`/files/${fileId}/thumbnail-url`)
		return response.data
	},

	// A short-lived pre-signed GET URL for downloading the file's binary.
	getDownloadUrl: async (fileId: string): Promise<string> => {
		const response = await apiClient.get<{ downloadUrl: string }>(`/files/${fileId}/download-url`)
		return response.data.downloadUrl
	},

	deleteFile: async (fileId: string): Promise<void> => {
		await apiClient.delete(`/files/${fileId}`)
	},

	// Retry ONE processing concern for an already-confirmed file. No re-upload,
	// and for `classification` no tuple resubmission — the server reuses what
	// was stored at confirm time. Used by both an upload tile's per-concern
	// retry and the admin diagnostics page's Reprocess button.
	reprocessConcern: async (fileId: string, concernId: string): Promise<void> => {
		await apiClient.post(`/files/${fileId}/reprocess/${concernId}`)
	},

	// SUPER_ADMIN diagnostics listing across all files. Returns the page's rows
	// together with the unpaginated `total`, which the page needs to say whether
	// what it is showing is the whole backlog or the first slice of it.
	listConcernsForAdmin: async (
		params: {
			status?: string
			staleOnly?: boolean
			sinceMinutes?: number
			fileId?: string
			page?: number
			limit?: number
		} = {},
	): Promise<{ rows: AdminConcernRow[]; total: number }> => {
		const query: Record<string, string> = {}
		if (params.status) query['filters[status]'] = params.status
		if (params.staleOnly) query['filters[stale]'] = 'true'
		if (params.sinceMinutes) query['filters[sinceMinutes]'] = String(params.sinceMinutes)
		if (params.fileId) query['filters[fileId]'] = params.fileId
		if (params.page) query.page = String(params.page)
		if (params.limit) query.limit = String(params.limit)
		const response = await apiClient.get<PaginatedResponse<AdminConcernRow>>('/admin/file-processing/concerns', {
			params: query,
		})
		if (response.status === 204 || !response.data) return { rows: [], total: 0 }
		return { rows: response.data.data ?? [], total: response.data.pagination?.total ?? 0 }
	},

	// Discover files by classification filter (axis code → value codes). `filters`
	// matches dimension axes (`geography`, `brand`) against a file's tags;
	// `attributeFilters` matches object_attribute axes (`order_code`, `aspect_ratio`)
	// against its stored attribute values. An empty filter returns the whole accessible
	// library. Each result carries its tags. `limit` defaults to the grid's page size; a
	// caller resolving one specific file by id (e.g. the asset-detail page) can pass a
	// higher cap to reduce (not eliminate) the risk of that file sitting past the default
	// page boundary. `page` is omitted from the request body entirely when not supplied,
	// so every existing caller that never passes it sends an identical request to before.
	discover: async (
		filters: AxisSelection,
		attributeFilters: AxisSelection = {},
		limit = 100,
		page?: number,
		/**
		 * `applies_in` asks which files COVER the context rather than which are filed under it —
		 * a file tagged `emea` applies in Spain, one tagged `emea_fr` does not. Omitted callers keep
		 * the browse semantics they already had.
		 */
		match?: 'filed_under' | 'applies_in',
	): Promise<FileWithTagsDTO[]> => {
		const base = { filters, attributeFilters, limit, ...(match ? { match } : {}) }
		const body = page === undefined ? base : { ...base, page }
		const response = await apiClient.post<FileWithTagsDTO[]>('/files/discover', body)
		// No match is answered as `200 []`; older deployments answered 204 with no body.
		return response.status === 204 || !response.data ? [] : response.data
	},
}
